import { FREE_BACKEND_CONFIG } from "@/config/runtime";

/**
 * Safe error extraction from API responses.
 * Handles FastAPI string errors AND Pydantic validation error arrays.
 */
export function getErrorMessage(error, fallback = "Une erreur est survenue") {
  const requestUrl = String(error?.config?.url || "");
  const status = error?.response?.status;
  const code = error?.response?.data?.code || error?.code;

  if (!error?.response && isNetworkLikeError(error)) {
    return getNetworkErrorMessage(error);
  }

  const statusMessage = getStatusMessage(status, code, requestUrl);
  if (statusMessage) return statusMessage;

  if (error?.response?.status === 404 && requestUrl.includes("/admin/system")) {
    return FREE_BACKEND_CONFIG.staleSystemRouteMessage;
  }

  const detail = error?.response?.data?.detail;

  if (!detail) return translateErrorMessage(error?.message || fallback);

  // Simple string error
  if (typeof detail === "string") return translateErrorMessage(detail);

  // Pydantic validation errors: [{type, loc, msg, input, url}, ...]
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (typeof e === "object" ? e.msg || JSON.stringify(e) : String(e)))
      .map(translateErrorMessage)
      .join("; ");
  }

  // Unknown object
  if (typeof detail === "object") return translateErrorMessage(detail.msg || detail.message || JSON.stringify(detail));

  return translateErrorMessage(String(detail));
}

function isNetworkLikeError(error) {
  return error?.message === "Network Error" || error?.code === "ECONNABORTED" || error?.code === "ERR_NETWORK";
}

function getNetworkErrorMessage(error) {
  const url = String(error?.config?.baseURL || error?.config?.url || "");
  if (error?.code === "ECONNABORTED") {
    return "Le serveur met trop de temps a repondre. Reessayez dans quelques secondes.";
  }
  if (url.includes("onrender.com")) {
    return FREE_BACKEND_CONFIG.networkErrorMessage;
  }
  return "Serveur inaccessible. Verifiez votre connexion internet puis reessayez.";
}

function getStatusMessage(status, code, requestUrl) {
  const codeMessages = {
    DATABASE_UNAVAILABLE: "Base de donnees indisponible. Reessayez dans quelques secondes.",
    DATABASE_OPERATION_FAILED: "Operation base de donnees indisponible pour le moment.",
  };
  if (codeMessages[code]) return codeMessages[code];

  if (status === 401) return "Session expiree. Reconnectez-vous pour continuer.";
  if (status === 403) return "Acces refuse. Votre compte n'a pas les droits necessaires.";
  if (status === 404) {
    if (requestUrl.includes("/files/") || requestUrl.includes("/preview") || requestUrl.includes("/source")) {
      return "Fichier inaccessible ou introuvable.";
    }
    return "Element introuvable.";
  }
  if (status === 408 || status === 504) return "Le serveur met trop de temps a repondre. Reessayez dans quelques secondes.";
  if (status === 413) return "Fichier trop volumineux.";
  if (status === 415) return "Type de fichier non accepte.";
  if (status === 422) return null;
  if (status === 429) return "Trop de tentatives. Patientez quelques minutes avant de reessayer.";
  if (status >= 500) return "Erreur serveur. Reessayez dans quelques secondes; si cela persiste, contactez un administrateur.";
  return null;
}

function translateErrorMessage(message) {
  const text = String(message || "").trim();
  const translations = {
    "Partner access required": "Acces partenaire requis",
    "Admin access required": "Acces administrateur requis",
    "Invalid credentials": "Identifiants invalides",
    "Invalid administrator password": "Mot de passe administrateur invalide",
    "Partner not found": "Partenaire introuvable",
    "Product not found": "Produit introuvable",
    "Order not found": "Commande introuvable",
    "Unauthorized": "Non autorise",
    "Forbidden": "Acces refuse",
    "Not Found": "Element introuvable",
    "Network Error": "Erreur reseau. Verifiez votre connexion puis reessayez.",
    "Too Many Requests": "Trop de tentatives. Patientez quelques minutes avant de reessayer.",
    "Invalid token": "Session invalide. Reconnectez-vous.",
    "User not found": "Compte introuvable. Reconnectez-vous.",
  };
  return translations[text] || text;
}

/**
 * Safe value for JSX rendering. Prevents "Objects are not valid as a React child".
 */
export function safeRender(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
  if (Array.isArray(value)) return value.map((v) => safeRender(v)).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
